import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import type { CartAddItemInput, CouponApplyInput, CheckoutInput } from "@mersa/shared";

export interface CartItemPackage {
  id: string;
  slug: string;
  name: string;
  shortDescription: string | null;
  price: number;
  oldPrice: number | null;
  thumbnailUrl: string | null;
}

export interface CartItemBook {
  id: string;
  slug: string;
  title: string;
  thumbnailUrl: string | null;
  price: number;
  oldPrice: number | null;
  physicalStock: number | null;
}

export interface CartItem {
  id: string;
  packageId: string | null;
  bookId: string | null;
  format: "DIGITAL" | "PHYSICAL" | null;
  quantity: number;
  addedAt: string;
  package: CartItemPackage | null;
  book: CartItemBook | null;
  lineTotal: number;
}

export interface Cart {
  items: CartItem[];
  subtotal: number;
  total: number;
}

export interface CouponResult {
  couponId: string;
  code: string;
  type: "PERCENT" | "FIXED";
  value: number;
  discountAmount: number;
  newTotal: number;
}

export const cartQueryKey = ["cart"];

export const useCart = () => {
  const authReady = useAuthStore((s) => s.authReady);
  return useQuery({
    queryKey: cartQueryKey,
    queryFn: () => api.get<Cart>("/api/cart", { auth: true }),
    enabled: authReady,
  });
};

export const useAddToCart = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CartAddItemInput) =>
      api.post<Cart>("/api/cart/items", input, { auth: true }),
    onSuccess: (data) => {
      queryClient.setQueryData(cartQueryKey, data);
    },
  });
};

export const useRemoveCartItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => api.del<Cart>(`/api/cart/items/${itemId}`, { auth: true }),
    onSuccess: (data) => {
      queryClient.setQueryData(cartQueryKey, data);
    },
  });
};

export const useApplyCoupon = () =>
  useMutation({
    mutationFn: (input: CouponApplyInput) =>
      api.post<CouponResult>("/api/cart/coupon", input, { auth: true }),
  });

export const useCheckout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CheckoutInput) =>
      api.post<{ id: string; merchantOid: string; totalAmount: number }>(
        "/api/orders/checkout",
        input,
        { auth: true }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cartQueryKey });
    },
  });
};

export const useMergeCartOnLogin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Cart>("/api/cart/merge", undefined, { auth: true }),
    onSuccess: (data) => {
      queryClient.setQueryData(cartQueryKey, data);
    },
  });
};
